import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabase, ref, get } from 'firebase/database';
import { FaArrowLeft, FaUser, FaCalendarAlt } from 'react-icons/fa';

const ReferralReport = () => {
    const { refCode } = useParams();
    const navigate = useNavigate();
    const [referredUsers, setReferredUsers] = useState([]);
    const [franchiseData, setFranchiseData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const db = getDatabase();

            // 1. Fetch Referred Users
            const referralRef = ref(db, `Matrimony/referrals/${refCode}`);
            const referralSnapshot = await get(referralRef);

            if (referralSnapshot.exists()) {
                const data = referralSnapshot.val();
                const usersArray = Object.values(data).sort((a, b) => b.timestamp - a.timestamp);
                setReferredUsers(usersArray);
            }

            // 2. Fetch Franchise Info to show who this report belongs to
            const franchisesRef = ref(db, 'Matrimony/franchises');
            const franchisesSnapshot = await get(franchisesRef);
            if (franchisesSnapshot.exists()) {
                const franchises = franchisesSnapshot.val();
                const owner = Object.values(franchises).find(f => f.referralCode === refCode);
                if (owner) setFranchiseData(owner);
            }

        } catch (error) {
            console.error('Error fetching referral data:', error);
        } finally {
            setLoading(true);
            // Artificial delay for smooth transition if needed, but let's just set false
            setLoading(false);
        }
    }, [refCode]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const styles = {
        container: {
            padding: '2rem',
            background: '#f7f7f7',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '2rem'
        },
        backButton: {
            padding: '0.8rem',
            borderRadius: '50%',
            border: 'none',
            background: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6a11cb',
            transition: 'transform 0.2s',
        },
        titleSection: {
            flex: 1
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: 'bold',
            color: '#1e293b',
            margin: 0
        },
        subtitle: {
            fontSize: '1rem',
            color: '#64748b',
            margin: '0.2rem 0 0 0'
        },
        statsRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
        },
        statCard: {
            background: 'white',
            padding: '1.5rem',
            borderRadius: '15px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
        },
        statLabel: {
            fontSize: '0.9rem',
            color: '#64748b',
            fontWeight: '600'
        },
        statValue: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#6a11cb'
        },
        tableContainer: {
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            overflow: 'hidden'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            textAlign: 'left'
        },
        th: {
            padding: '1.2rem',
            background: '#f1f5f9',
            color: '#475569',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
        },
        td: {
            padding: '1.2rem',
            borderBottom: '1px solid #f1f5f9',
            color: '#1e293b',
            fontSize: '1rem'
        },
        userCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem'
        },
        userIcon: {
            width: '35px',
            height: '35px',
            background: '#eef2ff',
            color: '#6366f1',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        empty: {
            padding: '4rem',
            textAlign: 'center',
            color: '#64748b'
        }
    };

    if (loading) return <div style={styles.container}>Loading report...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <button style={styles.backButton} onClick={() => navigate(-1)}>
                    <FaArrowLeft />
                </button>
                <div style={styles.titleSection}>
                    <h1 style={styles.title}>Referral Report</h1>
                    <p style={styles.subtitle}>
                        {franchiseData ? `Franchise: ${franchiseData.fullName} (${refCode})` : `Referral Code: ${refCode}`}
                    </p>
                </div>
            </div>

            <div style={styles.statsRow}>
                <div style={styles.statCard}>
                    <span style={styles.statLabel}>Total Referrals</span>
                    <span style={styles.statValue}>{referredUsers.length}</span>
                </div>
                {franchiseData && (
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>Franchise Owner</span>
                        <span style={styles.statValue}>{franchiseData.fullName}</span>
                    </div>
                )}
                <div style={styles.statCard}>
                    <span style={styles.statLabel}>Plan</span>
                    <span style={styles.statValue}>{franchiseData?.selectedPlan || 'N/A'}</span>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {referredUsers.length === 0 ? (
                    <div style={styles.empty}>
                        <p>No users have registered using this referral code yet.</p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>User Details</th>
                                <th style={styles.th}>Registration Date</th>
                                <th style={styles.th}>Contact Link</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referredUsers.map((user, idx) => (
                                <tr key={idx}>
                                    <td style={styles.td}>
                                        <div style={styles.userCell}>
                                            <div style={styles.userIcon}>
                                                <FaUser />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 'bold' }}>{user.fullName}</div>
                                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>ID: {user.userId}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <FaCalendarAlt style={{ color: '#94a3b8' }} />
                                            {formatDate(user.timestamp)}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <button
                                            onClick={() => navigate(`/admin/user-details/${user.userId}`)}
                                            style={{
                                                background: '#f1f5f9',
                                                border: 'none',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '8px',
                                                color: '#6366f1',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            View Profile
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ReferralReport;
